import { ReactNode, useState } from 'react';
import { SharedIcon } from '../../../SharedIcon/SharedIcon';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { PratusStepper } from 'components/DesignSystems/PratusStepper/PratusStepper';
import {
  ResourceRequest,
  ResourceRequestItem,
  ResourceRequestStatus,
  useUpdateResourceRequest,
} from 'components/IAP/hooks/incidentResourceRequestHooks';
import { Row } from 'react-bootstrap';
import { ApprovalModal } from './ApprovalModal';
import { FormProvider } from 'react-hook-form';
import { AddRequestedResource } from './AddRequestedResource';
import dayjs from 'dayjs';
import { SectionReqContent } from './SectionReqContent';
import { useResourceRequestForm } from 'components/IAP/ResourceRequests/hooks/useResourceRequestForm';
import { CustomResourcesDropdown } from './CustomResourcesDropdown';
import { NULL_VALUE } from 'constants/displayConstants';

type RequestCardProps = {
  request: ResourceRequest;
  memberPositions: { positionId: string; positionName: string }[];
  reqCount: Record<string, number>;
};

const getStep = (request: ResourceRequest) => {
  if (request.incident_commander_signature_timestamp) return 7;
  if (request.status === 'awaiting_incident_commander') return 7;
  if (request.finance_signature_timestamp) return 6;
  if (request.logistics_signature_timestamp) return 5;
  if (request.resl_signature_timestamp) return 4;
  if (request.section_chief_signature_timestamp) return 3;
  return 2;
};

const getStatusText = (request: ResourceRequest) => {
  switch (request.status as ResourceRequestStatus) {
    case 'draft':
      return 'Draft';
    case 'awaiting_approval':
      return 'Section Chief Review';
    case 'awaiting_review':
      return 'RESL Review';
    case 'awaiting_order':
      return 'Logistics Order';
    case 'awaiting_payment':
      return 'Finance Payment';
    case 'awaiting_incident_commander':
      return 'Incident Commander Approval';
    case 'awaiting_final':
      return 'Final Approval';
    case 'completed':
      return 'Approved';
    default:
      return NULL_VALUE;
  }
};

export const ApprovalSteps = [
  { id: 1, label: 'Request Resource' },
  { id: 2, label: 'Section Chief' },
  { id: 3, label: 'RESL Review' },
  { id: 4, label: 'Logistics' },
  { id: 5, label: 'Finance' },
  { id: 6, label: 'Area or District Command' },
];

const isApprovalStepDisabled = ({
  currentStep,
  memberPositions,
  requestStatus,
  targetStep,
}: {
  currentStep: number;
  memberPositions: { positionId: string; positionName: string }[];
  requestStatus: ResourceRequestStatus;
  targetStep: ApprovalViewStep;
}) => {
  if (targetStep === 2 && requestStatus === 'draft') {
    return true;
  }

  if (currentStep < targetStep) {
    return true;
  }

  return (
    currentStep === targetStep &&
    !memberPositions.some(
      (pos) => pos.positionName === approvalStepPositions[targetStep]
    )
  );
};

const getApprovalSteps = ({
  request,
  currentStep,
  memberPositions,
  onEdit,
  onViewStep,
}: {
  request: ResourceRequest;
  currentStep: number;
  memberPositions: { positionId: string; positionName: string }[];
  onEdit: () => void;
  onViewStep: (step: number) => void;
}) => {
  const skipped = (signature?: string) =>
    signature === 'Skipped' ? 'Skipped' : undefined;

  const viewButton = (targetStep: ApprovalViewStep): ReactNode => (
    <StylishNewButton
      onClick={() => onViewStep(targetStep)}
      disabled={isApprovalStepDisabled({
        currentStep,
        memberPositions,
        requestStatus: request.status,
        targetStep,
      })}
    >
      View
    </StylishNewButton>
  );

  const steps = [
    {
      id: 1,
      label: 'Request Resource',
      action: (
        <StylishNewButton
          onClick={() => {
            if (request.status === ('draft' as ResourceRequestStatus)) {
              onEdit();
            } else {
              onViewStep(1);
            }
          }}
        >
          {request.status === ('draft' as ResourceRequestStatus)
            ? 'Edit'
            : 'View'}
        </StylishNewButton>
      ),
    },
    {
      id: 2,
      label: 'Section Chief',
      subtitle: skipped(request.section_chief_signature),
      action: viewButton(2),
    },
    {
      id: 3,
      label: 'RESL Review',
      subtitle: skipped(request.resl_signature),
      action: viewButton(3),
    },
    {
      id: 4,
      label: 'Logistics',
      subtitle: skipped(request.logistics_signature),
      action: viewButton(4),
    },
    {
      id: 5,
      label: 'Finance',
      subtitle: skipped(request.finance_signature),
      action: viewButton(5),
    },
    {
      id: 6,
      label: 'Area or District Command',
      subtitle: skipped(request.final_signature),
      action: viewButton(6),
    },
  ];

  const showIcStep =
    request.status === 'awaiting_incident_commander' ||
    !!request.incident_commander_signature;

  if (showIcStep) {
    steps.push({
      id: 7,
      label: 'Incident Commander',
      subtitle: undefined,
      action: viewButton(7),
    });
  }

  return steps;
};

type ApprovalViewStep = 2 | 3 | 4 | 5 | 6 | 7;

const approvalStepPositions: Record<ApprovalViewStep, string> = {
  2: 'Operations Section Chief',
  3: 'Resource Unit Leader',
  4: 'Logistics Section Chief',
  5: 'Finance Section Chief',
  6: 'Area or District Command',
  7: 'Incident Commander',
};

export const RequestCard = ({
  request,
  memberPositions,
  reqCount,
}: RequestCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(0);
  const [editing, setEditing] = useState<boolean>(false);
  const [showAdd, setShowAdd] = useState(false);
  const [customResources, setCustomResources] = useState<ResourceRequestItem[]>(
    request?.items || []
  );
  const [logisticsItems, setLogisticsItems] = useState<ResourceRequestItem[][]>(
    request?.logistics_items || []
  );

  const currentStep = getStep(request);

  const { mutate: updateResourceRequest } = useUpdateResourceRequest();
  const { form, handleApprove, handleDeny } = useResourceRequestForm({
    requestData: request,
  });

  const handleDelete = (resourceRequest: ResourceRequest) => {
    updateResourceRequest({
      requestId: resourceRequest.resource_request_id,
      data: { ...resourceRequest, archived: true },
    });
  };
  const onSubmit = async () => {
    const items = customResources.map((resource) => ({
      ...resource,
      unit: 'unit',
      status: 'pending' as ResourceRequestItem['status'],
    }));
    updateResourceRequest({
      requestId: request.resource_request_id,
      data: {
        ...request,
        items,
        status: 'awaiting_approval' as ResourceRequestStatus,
        requestor_signature: 'requestor',
        requestor_signature_timestamp: new Date(),
      },
    });
    setEditing(false);
  };
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="resource-border">
          <div onClick={() => setExpanded(!expanded)} role="button">
            <div className="request-grid p-2 align-items-center">
              <div>
                {!expanded ? (
                  <SharedIcon iconName="keyboard_arrow_right" size="sm" />
                ) : (
                  <SharedIcon iconName="keyboard_arrow_down" size="sm" />
                )}
              </div>
              <div>{request.name}</div>
              <div>{request.requestor_name}</div>
              <div>{getStatusText(request)}</div>
              <div>
                {request.items?.map((item, idx) => (
                  <>
                    <div>
                      Kind:{item.kind}, Type: {item.type}, Priority:{' '}
                      {item.priority &&
                        item.priority.charAt(0).toUpperCase() +
                          item.priority.slice(1)}
                    </div>
                    <div>Description: {item.name}</div>
                    {request.items && idx < request.items.length - 1 && (
                      <hr className="m-0 me-2" />
                    )}
                  </>
                ))}
              </div>
              <div>
                <StylishNewButton
                  className="button--icon"
                  onClick={(e) => {
                    handleDelete(request);
                    e.stopPropagation();
                  }}
                >
                  <SharedIcon iconName="delete" />
                </StylishNewButton>
              </div>
            </div>
          </div>
          {expanded && (
            <div className="resource-card-content p-3">
              Approval Progress
              <div className="p-4">
                <PratusStepper
                  steps={getApprovalSteps({
                    request,
                    currentStep,
                    memberPositions,
                    onEdit: () => setEditing(!editing),
                    onViewStep: (step) => {
                      setShowModal(true);
                      setModalStep(step);
                    },
                  })}
                  activeStep={currentStep}
                />
              </div>
              {request.supply_sources && (
                <div className="border rounded p-2 mb-2">
                  <div>Suggested Sources of Supply & Substitutes</div>
                  <div className="border border-neutral-500 rounded p-2 mt-2">
                    {request.supply_sources}
                  </div>
                </div>
              )}
              {request.capability_enabled && (
                <div className="border rounded p-2 mb-2">
                  <div>Capability Enabled</div>
                  <div className="border border-neutral-500 rounded p-2 mt-2">
                    {request.capability_enabled}
                  </div>
                </div>
              )}
              <div className="border rounded p-2 mb-2">
                <div className="mb-3">
                  {request?.items?.length} Attached Resource(s)
                </div>
                {editing && (
                  <StylishNewButton
                    className="button button--secondary mb-2"
                    onClick={() => setShowAdd(!showAdd)}
                  >
                    Add Resource
                  </StylishNewButton>
                )}
                {showAdd && (
                  <AddRequestedResource
                    setCustomResources={setCustomResources}
                    setLogisticsItems={setLogisticsItems}
                    setShowAdd={setShowAdd}
                  />
                )}
                {editing &&
                  customResources?.map(
                    (resource: ResourceRequestItem, i: number) => (
                      <Resource
                        key={i}
                        resource={resource}
                        deletable={editing}
                        onDelete={() => {
                          setCustomResources((prev) =>
                            prev.filter((_, index) => index !== i)
                          );
                          // Also remove the corresponding logistics items to keep indices in sync
                          setLogisticsItems((prev) =>
                            prev.filter((_, index) => index !== i)
                          );
                        }}
                      />
                    )
                  )}
                {!editing &&
                  request?.items?.map((resource: ResourceRequestItem, i) => (
                    <Resource
                      key={i}
                      arrIndex={i}
                      resource={resource}
                      showLogisticsStep={true}
                      logisticsItems={logisticsItems[i] || []}
                      setLogisticsItems={setLogisticsItems}
                      reqCount={reqCount}
                      memberPositions={memberPositions}
                    />
                  ))}
              </div>
              <SectionReqContent memberPositions={memberPositions} />
              {editing && (
                <>
                  <StylishNewButton type="submit" className="me-2">
                    Resubmit to Section Chief
                  </StylishNewButton>
                  <StylishNewButton
                    className="button--secondary"
                    onClick={() => {
                      setEditing(false);
                      setCustomResources(request.items || []);
                    }}
                  >
                    Cancel
                  </StylishNewButton>
                </>
              )}
            </div>
          )}
          <ApprovalModal
            showModal={showModal}
            setShowModal={setShowModal}
            currentStep={currentStep}
            modalStep={modalStep}
            request={request}
            handleApprove={handleApprove}
            handleDeny={handleDeny}
            form={form}
            memberPositions={memberPositions}
            logisticsItems={logisticsItems}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export const Resource = ({
  resource,
  deletable,
  onEdit,
  onDelete,
  arrIndex,
  showLogisticsStep = false,
  logisticsItems = [],
  setLogisticsItems,
  reqCount,
  memberPositions,
}: {
  resource: ResourceRequestItem;
  deletable?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  arrIndex?: number;
  showLogisticsStep?: boolean;
  logisticsItems?: ResourceRequestItem[];
  setLogisticsItems?: React.Dispatch<
    React.SetStateAction<ResourceRequestItem[][]>
  >;
  reqCount?: Record<string, number>;
  memberPositions?: { positionId: string; positionName: string }[];
}) => {
  return (
    <div className="border border-neutral-500 rounded mb-3">
      <div className="p-3">
        <Row className="mb-2">
          <div className="col-1">
            <div>Quantity:</div> {resource.quantity || NULL_VALUE}
          </div>
          <div className="col-1">
            <div>Kind:</div> {resource.kind || NULL_VALUE}
          </div>
          <div className="col-1">
            <div>Type:</div> {resource.type || NULL_VALUE}
          </div>
          <div className="col-1">
            <div>Priority:</div> {resource.priority || NULL_VALUE}
          </div>
          <div className="col-2">
            <div>Description:</div> {resource.name || NULL_VALUE}
          </div>
          <div className="col-2">
            <div>Requested Reporting Location:</div>{' '}
            {resource.requested_reporting_location || NULL_VALUE}
          </div>
          <div className="col-2">
            <div>Requested Report Date:</div>{' '}
            {resource.requested_reporting_datetime
              ? dayjs(resource.requested_reporting_datetime).format(
                  'YYYY-MM-DD HH:mm'
                )
              : NULL_VALUE}
          </div>
          <div className="col-1">
            <div>Requested Demob Date:</div>{' '}
            {resource.requested_demob_datetime
              ? dayjs(resource.requested_demob_datetime).format(
                  'YYYY-MM-DD HH:mm'
                )
              : NULL_VALUE}
          </div>
          <div className="col-1">
            <div className="d-flex flex-wrap gap-2">
              {onEdit && (
                <StylishNewButton
                  className="button button--secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  aria-label="Edit resource"
                  title="Edit resource"
                >
                  <SharedIcon iconName="edit" />
                </StylishNewButton>
              )}
              {deletable && (
                <StylishNewButton
                  className="button button--secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.();
                  }}
                  aria-label="Delete resource"
                  title="Delete resource"
                >
                  <SharedIcon iconName="delete" />
                </StylishNewButton>
              )}
            </div>
          </div>
        </Row>
        {showLogisticsStep && setLogisticsItems && (
          <Row>
            <div className="col-12 mb-2">
              Attach Resources:
              <CustomResourcesDropdown
                arrIndex={arrIndex}
                logisticsItems={logisticsItems}
                setLogisticsItems={setLogisticsItems}
                reqCount={reqCount}
                isDisabled={
                  !memberPositions?.some(
                    (pos) => pos.positionName === 'Logistics Section Chief'
                  )
                }
              />
            </div>
          </Row>
        )}
      </div>
    </div>
  );
};
